import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import {
  withRouter,
  useHistory,
  useParams,
  NavLink,
  Link,
} from "react-router-dom";
import { DebounceInput } from "react-debounce-input";
import {
  ColumnHeader,
  TextColumn,
  DateColumn,
  ActionButton,
  AppTable,
} from "../../../components";
import DeleteModal from "../../../components/Common/DeleteModal";
import { ActionTypeEnum, RoleEnum } from "../../../enums";
import { PaginateQuery } from "../../../models";

import {
  useGetLectureAssignmentsQuery,
  useDeleteLectureAssignmentMutation,
  useToggleLectureAssignmentStateMutation,
} from "../../../store/api/lectureAssignmentApi";
import { AssignmentDto } from "../../../interfaces/lecture/assignment";
import { AddEditLectureAssignmentModal } from "./AddEditLectureAssignmentModal";
import { useGetUserProfileQuery } from "../../../store/api/accountApi";
import { useLazyGetLectureQuery } from "../../../store/api/lectureApi";
import { useLazyGetLessonQuery } from "../../../store/api/lessonApi";

export const LectureAssignments = () => {
  //#region hooks
  const { lectureId, lessonId } = useParams<any>();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery(10, "sequence_number:ASC");
  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>({
    ...initState,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [, setTogglingStateItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  //#endregion

  //#region api
  const {
    data: dataAssignments,
    isLoading: isLoadingAssignments,
    refetch,
  } = useGetLectureAssignmentsQuery({
    data: paginateQuery,
    lectureId,
  });
  const [deleteLectureAssignment, { isLoading: isLoadingDelete }] =
    useDeleteLectureAssignmentMutation();

  const [toggleLectureAssignmentState] =
    useToggleLectureAssignmentStateMutation();

  const { data: dataProfile } = useGetUserProfileQuery();
  const [getLesson, { data: dataLesson }] = useLazyGetLessonQuery();
  const [getLecture, { data: dataLecture }] = useLazyGetLectureQuery();
  //#endregion

  document.title = `${t("documentTitle", { pageName: "LectureAssignments" })}`;

  const columns: TableColumn<AssignmentDto>[] = [
    {
      name: <ColumnHeader title="sequence" />,
      cell: (row) => <TextColumn text={row.sequenceNumber + ""} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="title" />,
      cell: (row) => (
        <TextColumn text={row.title[i18n.language === "ar" ? "ar" : "en"]} />
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="questions" />,
      cell: (row) => (
        <>
          <TextColumn text={`${row.questionsCount || "0"}`} />
          &nbsp;
          <Link
            to={`${row.id}/questions`}
            title="Manage"
            className="link-secondary"
          >
            <span>({t("AddQuestion")})</span>
          </Link>
        </>
      ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      cell: (row) => (
        <React.Fragment>
          <ActionButton
            onAction={() => onEdit(row.id)}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          {dataProfile && dataProfile.role.key === RoleEnum.SUPER_ADMIN && (
            <>
              <ActionButton
                onAction={() => onToggleState(row.id)}
                actionType={ActionTypeEnum.ACTIVE_INACTIVE}
                state={row.isActive}
              />
              &nbsp;
            </>
          )}
          <ActionButton
            onAction={() => onDelete(row.id)}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "sequence_number":
        sortBy = 1;
        break;
      case "title.en":
      case "title.ar":
        sortBy = 2;
        break;
      case "created_at":
        sortBy = 3;
        break;
      default:
        sortBy = 1;
        break;
    }
    return sortBy;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("sequence_number:ASC"),
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  //#region effects

  useEffect(() => {
    const queryParams = new URLSearchParams(history.location.search);
    let query: PaginateQuery = {
      limit: queryParams.has("limit")
        ? Number(queryParams.get("limit"))
        : paginateQuery.limit,
      page: queryParams.has("page")
        ? Number(queryParams.get("page"))
        : paginateQuery.page,
      sortBy: queryParams.has("sortBy")
        ? (queryParams.get("sortBy") as string)
        : paginateQuery.sortBy,
      search: queryParams.has("search")
        ? (queryParams.get("search") as string)
        : paginateQuery.search,
    };
    if (
      queryParams.get("sortBy") &&
      queryParams.get("sortBy")?.split(":").length &&
      queryParams.get("sortBy")?.split(":").length == 2
    ) {
      setDefaultSortAsc(
        (queryParams.get("sortBy")?.split(":")[1] as string) === "ASC",
      );
      setDefaultSortFieldId(
        getSortByColumnName(queryParams.get("sortBy")?.split(":")[0] as string),
      );
    } else {
      setDefaultSortAsc(false);
      setDefaultSortFieldId(2);
    }
    setDefaultPage(query.page);
    setPaginateQuery({ ...paginateQuery, ...query });
  }, []);

  useEffect(() => {
    if (paginateQuery !== initState) {
      const { limit, page, search, sortBy } = paginateQuery;
      let query;
      if (page) query = `?page=${page}`;
      if (limit) query += `&limit=${limit}`;
      if (search && search !== "") {
        history.replace({
          search: `&search=${search}`,
        });
      }
      if (sortBy) query += `&sortBy=${sortBy}`;
      if (search) query += `&search=${search}`;
      history.replace({ search: query });
    }
  }, [paginateQuery]);

  useEffect(() => {
    if (lessonId) {
      getLesson(lessonId);
    }
  }, [lessonId]);

  useEffect(() => {
    if (lectureId) {
      getLecture(lectureId);
    }
  }, [lectureId]);

  //#endregion

  //#region handlers

  const onAdd = () => {
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onToggleState = (id: string) => {
    setTogglingStateItemId(id);
    toggleLectureAssignmentState(id);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteLectureAssignment(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {}
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "sequence_number";
        break;
      case 2:
        sortBy = "title" + "." + i18n.language;
        break;
      case 3:
        sortBy = "created_at";
        break;
      default:
        sortBy = "sequence_number";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<AssignmentDto>,
    sortDirection: string,
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  //#endregion

  return (
    <React.Fragment>
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div className="d-flex align-items-center flex-nowrap">
              <h4>
                <NavLink to={"statistics"} className="cursor-pointer">
                  {t("lectures")}
                </NavLink>
              </h4>
              {i18n.language === "ar" ? (
                <i className="ri-arrow-left-s-line align-bottom text-muted"></i>
              ) : (
                <i className="ri-arrow-right-s-line align-bottom text-muted"></i>
              )}
              <h4>
                <NavLink to={`../../${lessonId}`} className="cursor-pointer">
                  {i18n.language === "ar"
                    ? dataLesson?.name.ar
                    : dataLesson?.name.en}
                </NavLink>
              </h4>
              {i18n.language === "ar" ? (
                <i className="ri-arrow-left-s-line align-bottom text-muted"></i>
              ) : (
                <i className="ri-arrow-right-s-line align-bottom text-muted"></i>
              )}
              <h4 className="text-muted">
                {i18n.language === "ar"
                  ? dataLecture?.name.ar
                  : dataLecture?.name.en}
              </h4>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xxl={12}>
            <Card>
              <CardBody>
                <Row className="g-2">
                  <Col sm={4} className="mb-2">
                    <div className="search-box">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                          ) => onSearch(event.target.value)}
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </div>
                  </Col>
                  <Col className="col-sm-auto ms-auto">
                    <div className="list-grid-nav hstack gap-1">
                      <Button
                        color="success"
                        onClick={() => {
                          setPaginateQuery({ ...paginateQuery });
                          refetch();
                        }}
                      >
                        {t("refresh")}
                      </Button>
                      <Button color="success" onClick={() => onAdd()}>
                        <i className="ri-add-fill me-1 align-bottom"></i>
                        {t("AddAssignment")}
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <AppTable
                      columns={columns}
                      data={dataAssignments?.data || []}
                      progressPending={isLoadingAssignments}
                      onSort={onSort}
                      onChangeRowsPerPage={onChangeRowsPerPage}
                      onChangePage={onChangePage}
                      defaultSortFieldId={defaultSortFieldId}
                      paginationDefaultPage={defaultPage}
                      defaultSortAsc={defaultSortAsc}
                      paginationTotalRows={dataAssignments?.meta?.totalItems}
                    />
                  </Col>
                </Row>
                <DeleteModal
                  loading={isLoadingDelete}
                  show={showDeleteModal}
                  onDeleteClick={() => onDeleteConfirm()}
                  onCloseClick={() => {
                    setShowDeleteModal(false);
                  }}
                />
                <AddEditLectureAssignmentModal
                  lectureId={lectureId}
                  id={editingItemId}
                  show={showAddEditModal}
                  onCloseClick={() => {
                    setShowAddEditModal(false);
                    setEditingItemId(null);
                  }}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export const LectureAssignmentsPage = withRouter(LectureAssignments);
